import { useState } from "react";
import axios from "axios";
import { LogOut, RefreshCw } from "lucide-react";
import Seo from "@/components/Seo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STATUSES = ["new", "contacted", "quoted"];

const statusStyles = {
  new: "bg-[#2E5939] text-white",
  contacted: "bg-[#EAF3EC] text-[#2E5939] border border-[#2E5939]/30",
  quoted: "bg-[#16241C] text-white",
};

export default function Admin() {
  const [token, setToken] = useState(() => sessionStorage.getItem("ls_admin") || "");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [leads, setLeads] = useState(null);
  const [loading, setLoading] = useState(false);
  const [savingId, setSavingId] = useState("");

  const login = async (e) => {
    e.preventDefault();
    setError("");
    try {
      const { data } = await axios.post(`${API}/admin/login`, { password });
      sessionStorage.setItem("ls_admin", data.token);
      setToken(data.token);
      load(data.token);
    } catch {
      setError("Incorrect password.");
    }
  };

  const load = async (t = token) => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/leads`, {
        headers: { Authorization: `Bearer ${t}` },
      });
      setLeads(data.leads);
    } catch {
      sessionStorage.removeItem("ls_admin");
      setToken("");
    } finally {
      setLoading(false);
    }
  };

  const setStatus = async (leadId, status) => {
    setSavingId(leadId);
    try {
      await axios.patch(
        `${API}/admin/leads/${leadId}`,
        { status },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setLeads((prev) => prev.map((l) => (l.id === leadId ? { ...l, status } : l)));
    } catch {
      sessionStorage.removeItem("ls_admin");
      setToken("");
    } finally {
      setSavingId("");
    }
  };

  const logout = () => {
    sessionStorage.removeItem("ls_admin");
    setToken("");
    setLeads(null);
  };

  return (
    <div className="min-h-screen bg-[#EAF3EC] pt-[72px]" data-testid="admin-page">
      <Seo title="Client Login — Legacy Scapes" description="Estimate request inbox for Legacy Scapes." />
      <div className="mx-auto max-w-[1160px] px-5 py-14 md:px-8">
        {!token ? (
          <div className="mx-auto max-w-md rounded-2xl border border-[#16241C]/10 bg-white p-8" data-testid="admin-login-card">
            <h1 className="text-2xl font-extrabold tracking-tight text-[#16241C]">Client Login</h1>
            <p className="mt-2 text-sm text-[#5B6B60]">Estimate requests land here.</p>
            <form onSubmit={login} className="mt-6">
              <input
                type="password"
                data-testid="admin-password-input"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Password"
                className="w-full rounded-lg border border-[#16241C]/15 px-4 py-3 text-sm outline-none focus:border-[#2E5939]"
              />
              {error && <p className="mt-2 text-xs font-bold text-red-700" data-testid="admin-login-error">{error}</p>}
              <button
                type="submit"
                data-testid="admin-login-button"
                className="mt-4 w-full rounded-full bg-[#2E5939] py-3 text-sm font-bold text-white transition-colors hover:bg-[#23452B]"
              >
                Log in
              </button>
            </form>
          </div>
        ) : (
          <div>
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-extrabold tracking-tight text-[#16241C]" data-testid="admin-heading">
                  Estimate requests
                </h1>
                <p className="mt-1 text-sm text-[#5B6B60]">
                  {leads ? `${leads.length} request${leads.length === 1 ? "" : "s"}` : "Loading…"}
                </p>
              </div>
              <div className="flex gap-3">
                <button
                  onClick={() => load()}
                  data-testid="admin-refresh-button"
                  className="flex items-center gap-2 rounded-full border border-[#16241C]/15 bg-white px-5 py-2.5 text-xs font-bold text-[#16241C] transition-colors hover:bg-[#EAF3EC]"
                >
                  <RefreshCw size={14} /> Refresh
                </button>
                <button
                  onClick={logout}
                  data-testid="admin-logout-button"
                  className="flex items-center gap-2 rounded-full border border-[#16241C]/15 bg-white px-5 py-2.5 text-xs font-bold text-[#16241C] transition-colors hover:bg-[#EAF3EC]"
                >
                  <LogOut size={14} /> Log out
                </button>
              </div>
            </div>

            {leads === null && !loading && (
              <button onClick={() => load()} data-testid="admin-load-button" className="mt-8 rounded-full bg-[#2E5939] px-8 py-3 text-sm font-bold text-white">
                Load requests
              </button>
            )}

            <div className="mt-8 space-y-4" data-testid="admin-leads-list">
              {leads && leads.length === 0 && (
                <p className="rounded-2xl border border-[#16241C]/10 bg-white p-8 text-sm text-[#5B6B60]" data-testid="admin-empty-state">
                  No requests yet. New submissions from the contact form will show up here.
                </p>
              )}
              {leads &&
                leads.map((lead, i) => (
                  <div key={lead.id} className="rounded-2xl border border-[#16241C]/10 bg-white p-6" data-testid={`admin-lead-${i}`}>
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <p className="text-base font-extrabold text-[#16241C]">{lead.name}</p>
                      <span className="rounded-full bg-[#EAF3EC] px-3 py-1 text-xs font-bold text-[#2E5939]">{lead.service}</span>
                    </div>
                    <p className="mt-2 text-sm text-[#5B6B60]">
                      <a href={`tel:${lead.phone}`} className="font-bold text-[#2E5939]">{lead.phone}</a>
                      {" · "}
                      <a href={`mailto:${lead.email}`} className="font-bold text-[#2E5939]">{lead.email}</a>
                      {" · "}
                      {lead.address}
                    </p>
                    {lead.message && <p className="mt-3 text-sm leading-[1.6] text-[#16241C]">"{lead.message}"</p>}
                    <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
                      <div className="flex gap-2" data-testid={`admin-lead-status-${i}`}>
                        {STATUSES.map((s) => (
                          <button
                            key={s}
                            data-testid={`status-button-${s}-${i}`}
                            onClick={() => setStatus(lead.id, s)}
                            disabled={savingId === lead.id}
                            className={`rounded-full px-4 py-1.5 text-xs font-bold capitalize transition-colors duration-200 ${
                              lead.status === s
                                ? statusStyles[s]
                                : "border border-[#16241C]/15 bg-white text-[#5B6B60] hover:border-[#2E5939]/40 hover:text-[#2E5939]"
                            } disabled:opacity-50`}
                          >
                            {s}
                          </button>
                        ))}
                      </div>
                      <p className="text-xs text-[#5B6B60]">
                        {new Date(lead.created_at).toLocaleString("en-CA", { dateStyle: "medium", timeStyle: "short" })}
                      </p>
                    </div>
                  </div>
                ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
